import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isHoliday } from "../lib/holidays";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CalendarIcon, Send, Loader2, HomeIcon, AlertTriangle, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { hr as hrLocale } from "date-fns/locale";

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const CALENDAR_YEAR_START = 2026;
const CALENDAR_YEAR_END = 2027;
const MAX_WFH_DAYS_PER_MONTH = 4;
const MAX_WFH_DAYS_PER_WEEK = 1;

// 4 točke izjave — verbatim iz KIF Pravilnika o radu.
const IZJAVA_TOCKE = [
  "Posjedujem sredstva za rad (mobitel, računalo i dr.) i sve tehničke uvjete potrebne za obavljanje rada na daljinu (pristup električnoj energiji, internetu i dr.).",
  "Upoznat/a sam s pravilima zaštite na radu i sigurnosti na radu prilikom obavljanja rada na daljinu.",
  "Odgovorno ću postupati s podacima kojima raspolažem te ispunjavati sve obveze vezane uz informacijsku sigurnost, zaštitu tajnosti podataka i zaštitu osobnih podataka.",
  "Upoznat/a sam s odredbama Odluke o radu na daljinu i svim obvezama koje iz njega proizlaze.",
];

function isoWeekKey(d) {
  // ISO week (Mon-Sun). Returns "YYYY-Www" string for grouping.
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Thursday in current week decides the year
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

function workdaysInRange(start, end) {
  if (!start || !end) return [];
  const out = [];
  const cur = new Date(start);
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6 && !isHoliday(cur)) out.push(toISO(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function NewWFHRequest() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [razlog, setRazlog] = useState("");
  const [brojMobitela, setBrojMobitela] = useState("");
  const [izjavaPrihvacena, setIzjavaPrihvacena] = useState(false);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/wfh/my-monthly-usage").then(r => setUsage(r.data)).catch(() => {});
  }, []);

  const validation = useMemo(() => {
    if (!range.from || !range.to) return { valid: false, errors: [], warnings: [] };
    const wd = workdaysInRange(range.from, range.to);
    const errors = [];
    const warnings = [];
    if (wd.length === 0) errors.push("Odabrano razdoblje ne sadrži nijedan radni dan (samo vikend/blagdani).");
    if (wd.length > MAX_WFH_DAYS_PER_MONTH) errors.push(`Zahtjev sadrži ${wd.length} radnih dana — dozvoljeno je najviše ${MAX_WFH_DAYS_PER_MONTH}.`);

    // Intra-request Fri-Mon bridge check
    const wdSet = new Set(wd);
    for (const iso of wd) {
      const d = new Date(iso);
      if (d.getDay() === 5) { // Friday
        const mon = new Date(d); mon.setDate(d.getDate() + 3);
        if (wdSet.has(toISO(mon))) {
          errors.push(`Nije dozvoljeno povezivanje petka (${iso}) i sljedećeg ponedjeljka (${toISO(mon)}).`);
        }
      }
    }

    // Cross-request Fri-Mon (using usage.all_workdays)
    if (usage?.all_workdays?.length) {
      const existing = new Set(usage.all_workdays);
      for (const iso of wd) {
        const d = new Date(iso);
        if (d.getDay() === 5) {
          const mon = toISO(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 3));
          if (existing.has(mon)) errors.push(`Već imate aktivni zahtjev za ponedjeljak ${mon} — petak ${iso} ne može uz njega.`);
        }
        if (d.getDay() === 1) { // Monday
          const fri = toISO(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 3));
          if (existing.has(fri)) errors.push(`Već imate aktivni zahtjev za petak ${fri} — ponedjeljak ${iso} ne može uz njega.`);
        }
      }
    }

    // Monthly cap check
    if (usage?.all_months) {
      const monthCount = {};
      wd.forEach(iso => {
        const key = iso.slice(0, 7);
        monthCount[key] = (monthCount[key] || 0) + 1;
      });
      for (const [k, n] of Object.entries(monthCount)) {
        const existing = usage.all_months.find(m => m.month === k)?.used || 0;
        if (existing + n > MAX_WFH_DAYS_PER_MONTH) {
          const [y, m] = k.split("-");
          errors.push(`Mjesečni limit od ${MAX_WFH_DAYS_PER_MONTH} radna dana za ${m}/${y} premašen (već iskorišteno: ${existing}, traženo: ${n}).`);
        }
      }
    }

    // Weekly cap check (1 day per ISO week)
    const newWeekCount = {};
    wd.forEach(iso => {
      const d = new Date(iso);
      const wk = isoWeekKey(d);
      newWeekCount[wk] = (newWeekCount[wk] || 0) + 1;
    });
    const existingWeekCount = {};
    if (usage?.existing_days) {
      usage.existing_days.forEach(iso => {
        const d = new Date(iso);
        const wk = isoWeekKey(d);
        existingWeekCount[wk] = (existingWeekCount[wk] || 0) + 1;
      });
    }
    for (const [wk, n] of Object.entries(newWeekCount)) {
      const used = existingWeekCount[wk] || 0;
      if (used + n > MAX_WFH_DAYS_PER_WEEK) {
        errors.push(`Tjedni limit od ${MAX_WFH_DAYS_PER_WEEK} dana za ${wk} prekoračen (već iskorišteno: ${used}, traženo: ${n}).`);
      }
      if (n > MAX_WFH_DAYS_PER_WEEK) {
        errors.push(`U istom tjednu (${wk}) odabrali ste ${n} radna dana — dozvoljen je samo 1 dan po tjednu.`);
      }
    }

    return { valid: errors.length === 0 && wd.length > 0, errors, warnings, workdays: wd };
  }, [range, usage]);

  const phoneValid = /^[+\d][\d\s\-/()]{5,}$/.test(brojMobitela.trim());
  const canSubmit = validation.valid && phoneValid && izjavaPrihvacena && !busy;

  const submit = async () => {
    if (!validation.valid) {
      toast.error(validation.errors[0] || "Neispravna predaja");
      return;
    }
    if (!phoneValid) { toast.error("Unesite ispravan broj mobitela"); return; }
    if (!izjavaPrihvacena) { toast.error("Morate prihvatiti izjavu sa svim navedenim točkama"); return; }
    setBusy(true);
    try {
      await api.post("/wfh/requests", {
        start_date: toISO(range.from),
        end_date: toISO(range.to),
        razlog: razlog.trim(),
        broj_mobitela: brojMobitela.trim(),
        izjava_prihvacena: true,
      });
      toast.success("Zahtjev predan voditelju na suglasnost");
      nav("/zahtjevi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-wfh-page">
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-wider">Novi zahtjev</p>
        <h1 className="serif text-4xl font-semibold flex items-center gap-3">
          <HomeIcon className="w-8 h-8 text-accent" />
          Zahtjev za rad na daljinu
        </h1>
        <p className="text-muted-foreground mt-2">Predaja zahtjeva za rad na daljinu. Po suglasnosti voditelja ustrojstvene jedinice i glavnog tajnika fakulteta, izdaje se rješenje za rad na daljinu.</p>
        <div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-3">
          <span>Pravila: <b>najviše 4 radna dana mjesečno</b></span>
          <span>·</span>
          <span><b>1 dan po tjednu</b></span>
          <span>·</span>
          <span><b>Petak + idući ponedjeljak</b> ne mogu zajedno</span>
          <span>·</span>
          <span>Blagdani se ne broje</span>
          {usage?.current_month && (
            <span>· Iskorišteno ovaj mjesec: <b>{usage.current_month.used}/4</b></span>
          )}
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Razdoblje rada</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(() => {
            let rangeLabel = "Odaberi razdoblje (od – do)";
            if (range.from && range.to) rangeLabel = `${format(range.from,"dd.MM.yyyy")} – ${format(range.to,"dd.MM.yyyy")}`;
            else if (range.from) rangeLabel = format(range.from,"dd.MM.yyyy");
            return (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="wfh-pick-range-btn">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {rangeLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={2} locale={hrLocale}
                fromYear={CALENDAR_YEAR_START} toYear={CALENDAR_YEAR_END} defaultMonth={new Date()} />
            </PopoverContent>
          </Popover>
            );
          })()}
          {range.from && range.to && (
            <div className="text-xs text-muted-foreground">
              Radnih dana u odabranom razdoblju: <b>{validation.workdays?.length || 0}</b>
            </div>
          )}
          {validation.errors.length > 0 && (
            <div className="space-y-1 bg-destructive/10 border border-destructive/30 rounded p-3" data-testid="wfh-errors">
              {validation.errors.map((e) => (
                <div key={e} className="text-sm text-destructive flex gap-2 items-start">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
                  <span>{e}</span>
                </div>
              ))}
            </div>
          )}
          {range.from && range.to && validation.valid && (
            <div className="text-sm text-emerald-700 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded p-2">
              <CheckCircle2 className="w-4 h-4"/>
              Razdoblje je valjano za predaju.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Kontakt i napomena</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="wfh-broj-mobitela">Broj mobitela na koji ćete biti dostupni *</Label>
            <Input
              id="wfh-broj-mobitela"
              data-testid="wfh-broj-mobitela"
              type="tel"
              value={brojMobitela}
              onChange={e => setBrojMobitela(e.target.value)}
              placeholder="Npr. +385 91 234 5678"
              autoComplete="tel"
            />
            <p className="text-xs text-muted-foreground">Broj na koji ćete biti dostupni za vrijeme rada na daljinu (obavezno).</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="wfh-razlog">Napomena za voditelja (neobvezno)</Label>
            <Textarea id="wfh-razlog" data-testid="wfh-razlog" value={razlog} onChange={e => setRazlog(e.target.value)} placeholder="Eventualne dodatne informacije za voditelja ili tajništvo (npr. dostupnost, kontekst)" rows={3} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-accent/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-accent" />
            Izjava podnositelja
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">Podnošenjem ovog zahtjeva dajem sljedeću izjavu:</p>
          <ol className="space-y-2 text-sm pl-5 list-decimal" data-testid="wfh-izjava-list">
            {IZJAVA_TOCKE.map((t) => (
              <li key={t.slice(0, 48)}>{t}</li>
            ))}
          </ol>
          <label className="flex items-start gap-3 pt-3 cursor-pointer border-t" data-testid="wfh-izjava-label">
            <Checkbox
              id="wfh-izjava-checkbox"
              data-testid="wfh-izjava-checkbox"
              checked={izjavaPrihvacena}
              onCheckedChange={(v) => setIzjavaPrihvacena(v === true)}
              className="mt-0.5"
            />
            <span className="text-sm">
              Potvrđujem da su sve gore navedene točke izjave istinite i prihvaćam ih u cijelosti. *
            </span>
          </label>
        </CardContent>
      </Card>

      <Card className="bg-primary text-primary-foreground">
        <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-70">Sažetak</div>
            <div className="serif text-xl mt-1">
              {range.from && range.to ? `${format(range.from,"dd.MM.yyyy")} – ${format(range.to,"dd.MM.yyyy")}` : "Razdoblje nije odabrano"}
            </div>
            <div className="text-xs opacity-70 mt-1">Zahtjev se šalje voditelju: {user.manager_email || "—"}</div>
          </div>
          <Button size="lg" variant="secondary" onClick={submit} disabled={!canSubmit} data-testid="wfh-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Send className="w-4 h-4 mr-2"/>}
            Predaj zahtjev
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
